import { PercentItem } from '@/components/PercentItem';
import { Table, TableRef } from '@/components/Table';
import { WithLoader } from '@/components/WithLoader';
import { useConfigContext } from '@/context/configContext';
import {
    SuggestedBudgetCellRender,
    type SuggestedBudgetTableData,
} from '@/components/SuggestedBudgetCell/SuggestedBudgetCellRender';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { roundToDecimals } from '@/utils/numberUtils';
import { Typography, Button } from '@analytical-alley/ui';
import { IconDownload } from '@tabler/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';

const columnHelper = createColumnHelper<SuggestedBudgetTableData>();

export const SuggestedBudgetTable = () => {
    const {
        data: { suggestedBudgetData },
    } = useBudgetOptimizationContext();

    const { formatNumber } = useConfigContext();
    const { contributionFormatter } = useDashboardGlobalContext();

    const isLoading = false;
    const isFetching = false;

    const columns = useMemo(
        () => [
            columnHelper.accessor('category', {
                header: () => 'Category',
                cell: (info) => {
                    return <SuggestedBudgetCellRender {...info} disabled />;
                },
                footer: () => 'Total',
                meta: {
                    header: {
                        className: 'w-px',
                    },
                    footer: {
                        className: 'text-right',
                    },
                },
            }),
            columnHelper.accessor('variable', {
                cell: (info) => {
                    return <SuggestedBudgetCellRender {...info} disabled />;
                },
                header: () => <span>Variable</span>,
            }),
            columnHelper.accessor('previousPeriodInvestments', {
                header: () => (
                    <Typography tooltip="How much you invested in this channel in the last 12 months of the modelling period.">
                        Previous&nbsp;period investments
                    </Typography>
                ),
                cell: (info) => {
                    return (
                        <SuggestedBudgetCellRender
                            {...info}
                            className="justify-center"
                            valueTitle="Previous&nbsp;period investments"
                        >
                            {formatNumber(info.getValue())}
                        </SuggestedBudgetCellRender>
                    );
                },
                meta: {
                    header: {
                        className: 'w-px text-center',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                footer: (info) => {
                    return (
                        <span className="text-center">
                            {formatNumber(
                                info.table
                                    .getRowModel()
                                    .rows.reduce(
                                        (acc, row) =>
                                            acc +
                                            row.original
                                                .previousPeriodInvestments,
                                        0,
                                    ),
                            )}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('recommendedBudget', {
                header: () => (
                    <Typography tooltip="How much we recommend investing in this channel given the restrictions.">
                        Recommended budget
                    </Typography>
                ),
                cell: (info) => {
                    const previousPeriodInvestments =
                        info.row.original.previousPeriodInvestments;
                    const recommendedBudget = info.getValue();

                    const diff = recommendedBudget - previousPeriodInvestments;
                    const percent = roundToDecimals(
                        (diff / previousPeriodInvestments) * 100,
                        0,
                    );

                    return (
                        <SuggestedBudgetCellRender
                            {...info}
                            className="justify-center"
                            valueTitle="Recommended budget"
                            value={formatNumber(recommendedBudget)}
                        >
                            <div className="flex">
                                {formatNumber(recommendedBudget)}
                                <span className="w-12 ml-1 self-center flex justify-end">
                                    <PercentItem percent={percent} />
                                </span>
                            </div>
                        </SuggestedBudgetCellRender>
                    );
                },
                meta: {
                    header: {
                        className: 'w-px text-center',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                footer: (info) => {
                    const totalRecommendedBudget = info.table
                        .getRowModel()
                        .rows.reduce(
                            (acc, row) => acc + row.original.recommendedBudget,
                            0,
                        );
                    const totalPreviousPeriodInvestment = info.table
                        .getRowModel()
                        .rows.reduce(
                            (acc, row) =>
                                acc + row.original.previousPeriodInvestments,
                            0,
                        );

                    const diff =
                        totalRecommendedBudget - totalPreviousPeriodInvestment;

                    const percent = roundToDecimals(
                        (diff / totalPreviousPeriodInvestment) * 100,
                        0,
                    );

                    return (
                        <span className="text-center flex">
                            {formatNumber(
                                info.table
                                    .getRowModel()
                                    .rows.reduce(
                                        (acc, row) =>
                                            acc +
                                            row.original.recommendedBudget,
                                        0,
                                    ),
                            )}
                            <span className="w-12 ml-1 self-center flex justify-end">
                                <PercentItem percent={percent} />
                            </span>
                        </span>
                    );
                },
            }),
            columnHelper.accessor('contribution', {
                header: () => (
                    <Typography tooltip="How much this channel is predicted to generate over the selected period.">
                        Contribution
                    </Typography>
                ),
                meta: {
                    header: {
                        className: 'w-px text-center',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                cell: (info) => {
                    return (
                        <SuggestedBudgetCellRender
                            {...info}
                            className="justify-center"
                            valueTitle="Contribution"
                            value={contributionFormatter(info.getValue())}
                        >
                            {contributionFormatter(info.getValue())}
                        </SuggestedBudgetCellRender>
                    );
                },
                footer: (info) => {
                    const totalContribution = info.table
                        .getRowModel()
                        .rows.reduce(
                            (acc, row) => acc + row.original.contribution,
                            0,
                        );
                    return (
                        <span className="text-center">
                            {contributionFormatter(totalContribution)}
                        </span>
                    );
                },
            }),
        ],
        [formatNumber, contributionFormatter],
    );

    const tableRef = React.useRef<TableRef>(null);

    const downloadXls = useCallback(() => {
        // Calculate totals for export
        const getTotalsRow = () => {
            if (!suggestedBudgetData?.length) return undefined;

            const totalPreviousPeriodInvestments = suggestedBudgetData.reduce(
                (acc, row) => acc + row.previousPeriodInvestments,
                0,
            );

            const totalRecommendedBudget = suggestedBudgetData.reduce(
                (acc, row) => acc + row.recommendedBudget,
                0,
            );

            const totalContribution = suggestedBudgetData.reduce(
                (acc, row) => acc + row.contribution,
                0,
            );

            return {
                category: 'Total',
                variable: '',
                previousPeriodInvestments: formatNumber(
                    totalPreviousPeriodInvestments,
                ),
                recommendedBudget: formatNumber(totalRecommendedBudget),
                contribution: contributionFormatter(totalContribution),
            };
        };

        tableRef.current?.downloadXls(
            'suggested-budget-allocation',
            { addDate: true },
            getTotalsRow(),
        );
    }, [suggestedBudgetData, contributionFormatter, formatNumber]);

    return (
        <div className="glass-tile p-6">
            <div className="flex mb-4">
                <Typography
                    variant="titleS"
                    tooltip="Your optimisation summary. Compare historic spending against the recommended values and the predicted KPI contribution."
                >
                    Suggested budget allocation
                </Typography>
                <span className="grow" />
                <Button
                    className="glass-tile w-8 h-8 flex justify-center items-center p-2 rounded-md shadow-lg hover:bg-white/30 cursor-pointer"
                    onClick={downloadXls}
                    tooltip="Download the optimisation summary"
                >
                    <IconDownload />
                </Button>
            </div>
            <WithLoader
                className={isLoading ? 'min-h-96' : undefined}
                isLoading={isLoading}
                isFetching={isFetching}
            >
                <Table
                    tableRef={tableRef}
                    className="overflow-x-auto"
                    isLoading={isLoading}
                    data={suggestedBudgetData}
                    columns={columns}
                    onRowExport={({
                        recommendedBudget,
                        contribution,
                        previousPeriodInvestments,
                        ...restOfRow
                    }) => {
                        return {
                            ...restOfRow,
                            previousPeriodInvestments: formatNumber(
                                previousPeriodInvestments,
                            ),
                            recommendedBudget: formatNumber(recommendedBudget),
                            contribution: contributionFormatter(contribution),
                        };
                    }}
                    shouldSpanRow
                    colSpan={{
                        footer: {
                            value: 2,
                            index: 0,
                        },
                    }}
                />
            </WithLoader>
        </div>
    );
};
